/**
 * eSalon Consultation — Routine / Review & Submit (DEV-4285, step 3)
 *
 * Every candidate product is rendered server-side and hidden; this reveals the
 * recommended ones. Doing it that way keeps real Liquid product data (price,
 * image, variant id) available even though the rules run in the browser.
 *
 * Also drives the paired disclosure, the flip cards, the approach tablist and
 * the quantity steppers.
 */

class ConsultationRoutine extends HTMLElement {
  connectedCallback() {
    this.profile = this.dataset.profile || 'consultation';

    // Reached without finishing step 1? Leave now, before any of the setup
    // below runs — a routine derived from no answers must never be presented as
    // a personalised result, let alone reach the cart.
    const gate = window.esalonConsultation || {};
    if (gate.requireComplete && gate.requireComplete(this.profile, this.dataset.requireUrl)) return;

    this.nextUrl = this.dataset.nextUrl || '';
    // Shopify generates this one, so it carries the locale prefix. A path typed
    // into a setting does not — see the locale note in themes/CLAUDE.md.
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.backUrl = this.dataset.backUrl || '';

    this.panels = Array.from(this.querySelectorAll('[data-routine-panel]'));
    this.toggles = Array.from(this.querySelectorAll('[data-routine-toggle]'));
    this.disclosure = this.querySelector('[data-routine-disclosure]');
    this.flipCards = Array.from(this.querySelectorAll('[data-routine-flip]'));
    this.approachTabs = Array.from(this.querySelectorAll('[data-routine-approach-tab]'));
    this.approachPanels = Array.from(this.querySelectorAll('[data-routine-approach-panel]'));

    this.answers = this.readStored(`esalon:consultation:${this.profile}`) || {};

    this.applyRecommendation();
    this.renderProfileCards();
    this.renderPossessive();
    this.bindEvents();
  }

  readStored(key) {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      // Private browsing, a cleared store, or corrupt JSON — the page still
      // works, it just has nothing to restore.
      return null;
    }
  }

  /**
   * The routine, in order of preference:
   *   1. what the analyzing step stored, then
   *   2. re-derived from the answers if that is missing (direct link, cleared
   *      storage, someone refreshing after a while), then
   *   3. nothing — every product stays hidden and the empty state stands.
   */
  getRecommendation() {
    const stored = this.readStored(`esalon:recommendation:${this.profile}`);
    if (stored && stored.handles) return stored.handles;

    const recommender = (window.esalonRecommenders || {})[this.profile];
    if (!recommender) return null;

    try {
      return recommender.toHandles(recommender.recommend(this.answers));
    } catch (error) {
      console.warn('[Consultation] could not derive a routine', error);
      return null;
    }
  }

  applyRecommendation() {
    const handles = this.getRecommendation();
    if (!handles) {
      // No routine at all: the formula cards describe products nobody has been
      // recommended, so the band goes rather than standing empty.
      this.applyFormula(new Set());
      return;
    }

    // ONLY `main` is shown. `fou` (the shampoo + conditioner pair) is not part of
    // the recommended routine — in the legacy view it is a cross-sell gated on
    // `$all_recommended_subscribed && !$all_fou_subscribed`
    // (View/GrowthProfile/3.ctp:377), i.e. shown only to someone already
    // subscribed to every main product but not to those two. A first-time
    // visitor has an empty plan, so that condition is false and they see three
    // products, not five.
    //
    // The engine still returns `fou` — it is faithful to the rules, and the
    // pair is needed once subscription state exists on the Shopify side. It is
    // just not rendered here.
    const ordered = [...(handles.main || [])];
    this.recommendedHandles = ordered;
    this.upsellHandles = [...(handles.fou || [])];

    const shown = new Set(ordered);

    this.querySelectorAll('[data-routine-product]').forEach((node) => {
      // A swap card stands for several products (the gummies and the capsules
      // are one card), so it shows when ANY of its members is recommended, and
      // opens on whichever that is rather than on its first.
      const members = (node.dataset.routineGroupMembers || '').split(',').filter(Boolean);

      if (!members.length) {
        const handle = node.dataset.routineProduct;
        node.hidden = !shown.has(handle);
        // Runs for a group of ONE too. It is what hoists the variant id off the
        // price block onto the card, which getSelection() reads — skip it here
        // and every non-swap product carts with a null variant.
        if (!node.hidden) this.selectSwapMember(node, handle);
        return;
      }

      const active = members.find((handle) => shown.has(handle));
      node.hidden = !active;
      if (active) this.selectSwapMember(node, active);
    });

    this.orderByRecommendation(ordered);
    this.applyFormula(shown);
    this.syncQuantities();

    const [firstTab] = this.visibleApproachTabs();
    if (firstTab) this.selectApproachTab(firstTab);
  }

  /**
   * The formula cards explain ingredients, not products, so the mapping is
   * many-to-one: EITHER serum lights up "Procapil and Redensyl", and gummies
   * OR multivitamins light up "Biotin". Each block declares its own trigger
   * handles, which is the three `in_array` checks in the legacy
   * `formula_swiper.ctp` moved into content.
   *
   * Order comes from the blocks, not from the recommendation — the legacy view
   * builds `$show_formula` in a literal minoxidil / serum / supplement order
   * too, so these are deliberately NOT run through orderByRecommendation().
   */
  applyFormula(shown) {
    let visible = 0;

    this.querySelectorAll('[data-routine-formula]').forEach((item) => {
      const match = (item.dataset.routineFormula || '')
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean)
        .some((handle) => shown.has(handle));

      item.hidden = !match;
      if (match) visible += 1;
    });

    // A heading with nothing under it reads worse than no section at all.
    const section = this.querySelector('[data-routine-formula-section]');
    if (section) section.hidden = visible === 0;
  }

  /**
   * Reflect the recommendation's order in the DOM.
   *
   * `order` on a flex/grid child would move things visually while leaving the
   * tab sequence and screen-reader order untouched, so the node is moved
   * instead — the two stay in step.
   */
  orderByRecommendation(ordered) {
    const containers = new Set();
    this.querySelectorAll('[data-routine-product]').forEach((node) => {
      if (node.parentElement) containers.add(node.parentElement);
    });

    containers.forEach((container) => {
      ordered.forEach((handle) => {
        const node = this.cardForHandle(container, handle);
        if (node) container.appendChild(node);
      });
    });
  }

  /**
   * A swap card stands for several products and its `data-routine-product` is
   * only the group's FIRST member, so a recommendation naming the other member
   * finds nothing by that attribute — the card then keeps its authored position
   * while everything else is reordered around it. Fall back to the member list.
   */
  cardForHandle(container, handle) {
    const direct = container.querySelector(`[data-routine-product="${handle}"]`);
    if (direct) return direct;

    return (
      Array.from(container.querySelectorAll('[data-routine-group-members]')).find((node) =>
        (node.dataset.routineGroupMembers || '')
          .split(',')
          .map((member) => member.trim())
          .includes(handle)
      ) || null
    );
  }

  /**
   * Switch a swap card to one of its member products: content, price, the
   * radio, the handle the cart will read, and where "Learn more" points.
   */
  selectSwapMember(card, handle) {
    card.dataset.routineActiveHandle = handle;

    // The variant id lives on the price block, the only member element that has
    // one, so the active member's is hoisted onto the card for getSelection().
    let variantId = '';
    let personalized = null;

    card.querySelectorAll('[data-swap-member]').forEach((node) => {
      const active = node.dataset.swapMember === handle;
      node.hidden = !active;
      if (!active) return;

      if (node.dataset.variantId) variantId = node.dataset.variantId;
      if (node.dataset.personalized) personalized = node.dataset.personalized;
    });

    // Assigned on EVERY path, exactly like the variant id above. A member that
    // declares nothing must reset the card rather than inherit the previous
    // member's value — inheriting would print the customer's name on a product
    // that is not personalised, which is the same class of bug as the stale
    // variant id.
    card.dataset.routinePersonalized = personalized === 'true' ? 'true' : 'false';

    // Assigned unconditionally, INCLUDING the empty case. A member only carries
    // a variant id when its product resolves, so switching to one that is
    // unpublished, deleted or sold out must clear the card — leaving the
    // previous member's id behind carts that product under this one's handle,
    // and the missing-variant guard in addToCart() cannot see it because the id
    // is present, just wrong.
    if (variantId) card.dataset.variantId = variantId;
    else delete card.dataset.variantId;

    card.querySelectorAll('[data-routine-swap-input]').forEach((input) => {
      input.checked = input.value === handle;
    });

    const learn = card.querySelector('[data-routine-learn-more]');
    if (learn) learn.dataset.routineLearnMore = handle;
  }

  /** Parses a block's "value|Label" lines into a lookup. */
  parseLabels(raw) {
    const map = {};
    (raw || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const [value, label] = line.split('|');
        if (value) map[value.trim()] = (label || value).trim();
      });
    return map;
  }

  /** Writes each customer's own answers onto the front of their profile cards. */
  renderProfileCards() {
    this.flipCards.forEach((card) => {
      const target = card.querySelector('[data-routine-answers]');
      if (!target) return;

      const key = card.dataset.answerKey;
      const labels = this.parseLabels(card.dataset.answerLabels);
      const answer = this.answers[key];
      const values = Array.isArray(answer) ? answer : [answer].filter(Boolean);

      const text = values
        .map((value) => labels[value] || value)
        .filter(Boolean)
        .join('\n');

      if (text) target.textContent = text;

      this.renderCardIcon(card, values[0]);
    });
  }

  /**
   * The icon reflects the FIRST selected answer only, matching the legacy view
   * (`reset($growth_profile[2][...])` in flip_cards.ctp). URLs arrive already
   * resolved from Liquid as `value|url` pairs separated by `||`.
   */
  renderCardIcon(card, firstValue) {
    const wrap = card.querySelector('[data-routine-card-icon]');
    if (!wrap) return;

    const icons = {};
    (card.dataset.answerIcons || '')
      .split('||')
      .map((pair) => pair.trim())
      .filter(Boolean)
      .forEach((pair) => {
        const split = pair.indexOf('|');
        if (split > 0) icons[pair.slice(0, split).trim()] = pair.slice(split + 1).trim();
      });

    const url = icons[firstValue];
    if (!url) return;

    const img = document.createElement('img');
    img.className = 'esalon-consultation-routine__card-icon';
    img.src = url;
    img.alt = '';
    img.width = 65;
    img.height = 65;
    img.loading = 'lazy';
    img.decoding = 'async';
    wrap.replaceChildren(img);
  }

  /** Fills the "Greg's" prefix on personalized products. */
  renderPossessive() {
    const name = (this.answers.name || '').trim();
    if (!name) return;

    // Matches the legacy possessive: "Chris" -> "Chris'", everything else -> "'s".
    const possessive = /s$/i.test(name) ? `${name}'` : `${name}'s`;
    this.querySelectorAll('[data-routine-possessive]').forEach((node) => {
      node.textContent = `${possessive} `;
    });
  }

  bindEvents() {
    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => this.onToggle(toggle));
    });

    this.flipCards.forEach((card) => {
      card.addEventListener('click', () => {
        card.setAttribute('aria-pressed', card.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      });
    });

    this.approachTabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectApproachTab(tab));
      tab.addEventListener('keydown', (event) => this.onApproachKeydown(event));
    });

    this.querySelectorAll('[data-routine-swap-input]').forEach((input) => {
      input.addEventListener('change', () => {
        const card = input.closest('[data-routine-product]');
        if (card) this.selectSwapMember(card, input.value);
      });
    });

    this.querySelectorAll('[data-routine-qty]').forEach((button) => {
      button.addEventListener('click', () => {
        this.changeQuantity(button, Number.parseInt(button.dataset.routineQty, 10));
      });
    });

    const back = this.querySelector('[data-routine-back]');
    if (back) {
      back.addEventListener('click', () => {
        if (this.backUrl) this.navigate(this.backUrl);
        else window.history.back();
      });
    }

    const cont = this.querySelector('[data-routine-continue]');
    if (cont) cont.addEventListener('click', () => this.submit());
  }

  /**
   * Opening is shared, closing is not — the live behaviour.
   *
   * From fully collapsed, a click on EITHER toggle opens both: the two are one
   * "show me my results" gesture at that point. Once anything is open the two
   * behave as independent disclosures, so a click closes only its own panel and
   * the customer can end up with one open and one shut. Collapsing the last
   * open panel returns to the shared state, and the next click opens both
   * again.
   */
  onToggle(toggle) {
    const anyOpen = this.toggles.some((node) => node.getAttribute('aria-expanded') === 'true');

    if (!anyOpen) {
      this.toggles.forEach((node) => this.setPanelState(node, true));
      this.syncDisclosureLayout();
      return;
    }

    this.setPanelState(toggle, toggle.getAttribute('aria-expanded') !== 'true');
    this.syncDisclosureLayout();
  }

  /**
   * Row while everything is collapsed, column once anything is open — the live
   * `.accordion-container.vertical-flex` switch. In column mode a collapsed
   * 345px toggle sits centred ABOVE an expanded one rather than beside it.
   */
  syncDisclosureLayout() {
    if (!this.disclosure) return;

    const anyOpen = this.toggles.some((node) => node.getAttribute('aria-expanded') === 'true');
    this.disclosure.classList.toggle('esalon-consultation-routine__disclosure--stacked', anyOpen);
  }

  setPanelState(toggle, open) {
    toggle.setAttribute('aria-expanded', String(open));

    const panel = document.getElementById(toggle.dataset.routineToggle || '');
    if (panel) panel.hidden = !open;
  }

  /**
   * Read fresh from the DOM every time, NOT from the array captured in
   * connectedCallback. The tabs carry `data-routine-product`, so
   * orderByRecommendation() physically moves them into recommendation order —
   * the cached array is authored order, which is a different sequence. Using it
   * put the 40/35/25 grading and the two tints on the wrong rows, and made
   * ArrowDown jump over a tab.
   */
  visibleApproachTabs() {
    return Array.from(this.querySelectorAll('[data-routine-approach-tab]')).filter(
      (tab) => !tab.hidden
    );
  }

  selectApproachTab(tab) {
    const handle = tab.dataset.routineProduct;

    this.approachTabs.forEach((node) => {
      const selected = node === tab;
      node.setAttribute('aria-selected', String(selected));
      node.tabIndex = selected ? 0 : -1;
    });

    this.approachPanels.forEach((panel) => {
      panel.hidden = panel.dataset.routineProduct !== handle;
    });

    this.paintApproachTabs();
  }

  /**
   * The live widget grades the tab column 40 / 35 / 25 and tints the two
   * UNSELECTED tabs differently — the first #f4f6f8, the second #e9f2fb. That
   * is what the `.step1-active` / `.step2-active` / `.step3-active` blocks in
   * `_tab_table.scss` encode: which tab gets which tint moves with the
   * selection, it is not fixed per row.
   *
   * Both depend on position among the VISIBLE tabs, and six of the nine
   * products are hidden — plus `orderByRecommendation` moves the recommended
   * three to the end of the container. So a CSS `:nth-of-type` counts the
   * wrong siblings, which is why the grading and the tints were landing on
   * hidden tabs. Only JS knows the real order.
   */
  paintApproachTabs() {
    let unselected = 0;

    this.visibleApproachTabs()
      .forEach((tab, index) => {
        tab.dataset.approachStep = String(index + 1);

        if (tab.getAttribute('aria-selected') === 'true') {
          tab.dataset.approachTone = 'selected';
        } else {
          tab.dataset.approachTone = unselected === 0 ? 'first' : 'second';
          unselected += 1;
        }
      });
  }

  /** Arrow-key movement between tabs, as expected of a tablist. */
  onApproachKeydown(event) {
    const keys = ['ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const visible = this.visibleApproachTabs();
    if (!visible.length) return;

    const current = visible.indexOf(event.target);
    let next = current;

    if (event.key === 'ArrowDown') next = (current + 1) % visible.length;
    if (event.key === 'ArrowUp') next = (current - 1 + visible.length) % visible.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = visible.length - 1;

    event.preventDefault();
    this.selectApproachTab(visible[next]);
    visible[next].focus();
  }

  /**
   * The floor is per product. Most can be taken to zero, which keeps the card
   * on the page in a faded state so it can be added back; the treatment anchors
   * the routine and declares `min_quantity: 1`, so its minus is disabled from
   * the start.
   */
  minQuantity(item) {
    const declared = Number.parseInt(item.dataset.routineMinQuantity, 10);
    return Number.isNaN(declared) ? 0 : declared;
  }

  /**
   * Absent means uncapped, NOT zero — a missing attribute must never be able to
   * pin every stepper shut.
   */
  maxQuantity(item) {
    const declared = Number.parseInt(item.dataset.routineMaxQuantity, 10);
    return Number.isNaN(declared) ? Infinity : declared;
  }

  quantityOf(item) {
    const output = item.querySelector('[data-routine-qty-value]');
    // NOT `|| 0` — a parsed 0 is falsy and would read as "no value", which is
    // exactly the quantity this now has to represent.
    const parsed = Number.parseInt(output ? output.textContent : '', 10);
    return Number.isNaN(parsed) ? this.minQuantity(item) : parsed;
  }

  changeQuantity(button, delta) {
    const item = button.closest('[data-routine-product]');
    if (!item) return;

    const next = this.quantityOf(item) + delta;
    const clamped = Math.min(this.maxQuantity(item), Math.max(this.minQuantity(item), next));

    this.setQuantity(item, clamped);
  }

  setQuantity(item, quantity) {
    const output = item.querySelector('[data-routine-qty-value]');
    if (output) output.textContent = String(quantity);

    const minus = item.querySelector('[data-routine-qty="-1"]');
    const plus = item.querySelector('[data-routine-qty="1"]');

    if (minus) minus.disabled = quantity <= this.minQuantity(item);
    if (plus) plus.disabled = quantity >= this.maxQuantity(item);

    // Stepping to a bound disables the very button that was just pressed, which
    // drops focus to <body> and throws a keyboard user back to the top of the
    // document. Hand focus to the opposite button, which is always live at a
    // bound and is where they would go next anyway.
    const active = document.activeElement;
    if (active === minus && minus.disabled && plus && !plus.disabled) plus.focus();
    else if (active === plus && plus.disabled && minus && !minus.disabled) minus.focus();

    const zeroed = quantity === 0;
    item.classList.toggle('esalon-consultation-routine__builder-item--zero', zeroed);

    // A zeroed card is an INACTIVE component, not merely a dimmed one. Leaving
    // its controls live meant a "Learn more" link sitting at 2.7:1 contrast that
    // still responded to clicks; disabling them makes the faded state honest and
    // takes them out of the tab order.
    item
      .querySelectorAll('[data-routine-learn-more], [data-routine-swap-input]')
      .forEach((control) => {
        control.disabled = zeroed;
      });
  }

  /** Paints each card's stepper into a state consistent with its own floor. */
  syncQuantities() {
    this.querySelectorAll('[data-routine-min-quantity]').forEach((item) => {
      this.setQuantity(item, this.quantityOf(item));
    });
  }

  /** Everything the cart step needs, without re-deriving any of it. */
  getSelection() {
    return Array.from(this.querySelectorAll('[data-routine-product]:not([hidden])'))
      .filter((node) => node.matches('.esalon-consultation-routine__builder-item'))
      .map((node) => {
        return {
          // The active handle, so a card switched to capsules carts capsules.
          handle: node.dataset.routineActiveHandle || node.dataset.routineProduct,
          variantId: node.dataset.variantId || null,
          personalized: node.dataset.routinePersonalized === 'true',
          quantity: this.quantityOf(node),
        };
      })
      // A card taken to zero stays on the page so it can be added back, but it
      // is not part of the order.
      .filter((line) => line.quantity > 0);
  }

  submit() {
    const items = this.getSelection();

    const event = new CustomEvent('consultation:routine-submit', {
      bubbles: true,
      cancelable: true,
      detail: { profile: this.profile, answers: this.answers, items },
    });
    this.dispatchEvent(event);

    // Cancelling lets an outside handler own the submit entirely.
    if (event.defaultPrevented) return;

    this.addToCart(items);
  }

  /**
   * Removes the lines a previous run of this consultation left in the cart, so
   * adding REPLACES rather than stacks.
   *
   * This reconciles against the real cart instead of trusting a local flag, and
   * it is what makes Continue idempotent. A sessionStorage latch cannot work
   * here, for two reasons that pull in opposite directions:
   *
   *   - The cart lives server-side. Add the routine, empty the cart, press
   *     Continue: a latch short-circuits and lands the shopper on an empty cart
   *     with no add and no explanation.
   *   - /cart/add.js only merges lines whose variant AND properties both match.
   *     Re-taking the quiz changes `_<profile>_payload` on every line, so a
   *     second add does not merge — it stacks a whole second routine.
   *
   * Matching on the payload property rather than on variant ids is deliberate:
   * it removes only what this consultation added, leaving a line the customer
   * added themselves from a product page alone.
   *
   * ONE /cart/update.js call, not a change.js call per line. Shopify's docs
   * nudge toward change.js for lines already in the cart, but change.js takes
   * only one line at a time, and a sequence of them is not safe here: a line
   * item `key` is explicitly NOT persistent — the Cart API reference states it
   * "changes as characteristics of the line item change... including but not
   * limited to properties and discount applications". Removing one line can
   * therefore re-apply cart-level or automatic discounts across the survivors
   * and invalidate the keys we snapshotted from the GET, so a later call in the
   * loop posts a stale key and silently does nothing. A single `updates` object
   * is evaluated against one cart state and closes that window.
   *
   * Keyed by `key` and never by `variant_id`: update.js ADDS a line when a
   * variant id matches nothing in the cart, which would turn a removal into an
   * add. Keys are sent as strings, which the same reference requires.
   *
   * Returns the lines it removed so the caller can put them back if the add
   * that follows fails. Throws if the cart cannot be read or the removal cannot
   * be confirmed — the caller MUST NOT add on top of an unknown cart state.
   */
  async removePreviousRoutine() {
    const routes = window.routes || {};
    const cartUrl = routes.cart_url || '/cart';
    const updateUrl = routes.cart_update_url || '/cart/update.js';
    const payloadKey = `_${this.profile}_payload`;
    const mine = (cart) =>
      (cart.items || []).filter((line) => line.properties && line.properties[payloadKey]);

    const response = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
    });
    // Throws rather than returning: falling through to the add would stack a
    // second full-price routine with nothing shown to the customer.
    if (!response.ok) throw new Error(`Could not read the cart (HTTP ${response.status})`);

    const ours = mine(await response.json());
    if (!ours.length) return [];

    const updates = {};
    for (const line of ours) updates[String(line.key)] = 0;

    const removed = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!removed.ok) throw new Error(`Could not clear the previous routine (HTTP ${removed.status})`);

    // The status alone is not enough. A stale key is not an error to Shopify —
    // it updates nothing and still answers 200 — so the only way to know the
    // removal actually happened is to look at the cart it returns.
    const after = await removed.json().catch(() => null);
    if (after && mine(after).length) {
      throw new Error('The previous routine is still in the cart');
    }

    return ours;
  }

  /**
   * Puts back what removePreviousRoutine() took out, after a failed add.
   *
   * Best effort: it reports failure by warning, never by throwing, because it
   * runs inside the catch of the real error and must not replace it.
   *
   * The guard matters more than the restore. A network failure mid-add is
   * ambiguous — Shopify may well have committed it — so this re-reads the cart
   * first and does nothing if the new routine is already there. Skipping that
   * check would add the OLD routine on top of the new one, which is the exact
   * double-charge this whole path exists to prevent.
   */
  async restorePreviousRoutine(lines) {
    if (!lines || !lines.length) return;

    const routes = window.routes || {};
    const cartUrl = routes.cart_url || '/cart';
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const payloadKey = `_${this.profile}_payload`;

    try {
      const current = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (current.ok) {
        const cart = await current.json();
        const present = (cart.items || []).some(
          (line) => line.properties && line.properties[payloadKey]
        );
        if (present) return;
      }

      await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines.map((line) => ({
            id: line.variant_id,
            quantity: line.quantity,
            properties: line.properties,
          })),
        }),
      });
    } catch (error) {
      console.warn('[Consultation] could not restore the previous routine', error);
    }
  }

  /**
   * Adds the whole routine in ONE /cart/add.js call.
   *
   * One call, not one per product: Shopify's Ajax cart is not transactional
   * across requests, so a sequence of adds can leave a half-built routine in
   * the cart when the third one fails. `items` is all-or-nothing.
   *
   * The consultation answers ride along as line item properties on EVERY line.
   * Duplicated on purpose — they are per-line attributes, so a customer who
   * removes one product from the cart must not take the profile with it.
   */
  async addToCart(items) {
    const cont = this.querySelector('[data-routine-continue]');

    const lines = items.filter((line) => line.variantId);

    // Two distinct states, and they must not share a message: "we cannot sell
    // you these yet" versus "you removed everything".
    if (!lines.length && !items.length) {
      this.setStatus(this.dataset.emptyMessage || '', true);
      return;
    }

    // A product with no variant has not been created in the catalog yet
    // (DEV-4218). Better to say so than to post a payload Shopify will reject
    // with an opaque error, or — worse — to cart a partial routine.
    if (lines.length !== items.length || !lines.length) {
      this.setStatus(this.dataset.unavailableMessage || '', true);
      return;
    }

    // Built PER LINE, not once and shared: the visible `Name` belongs only on
    // products that are actually personalised. The private keys are identical on
    // every line, so the full profile still reaches the order either way.
    const build = (window.esalonConsultation || {}).buildLineItemProperties;

    // Without it every line would post `properties: {}` — an order carrying no
    // consultation data at all, indistinguishable from a good one until
    // fulfilment. Better to fail visibly.
    if (!build) {
      console.warn('[Consultation] the properties module did not load; refusing to add');
      this.setStatus(this.dataset.errorMessage || '', true);
      return;
    }

    const payload = {
      items: lines.map((line) => ({
        id: Number(line.variantId),
        quantity: line.quantity,
        properties: build(this.profile, this.answers, { showName: line.personalized }),
      })),
    };

    this.setBusy(cont, true);
    this.setStatus('');

    // Held outside the try so the catch can put back whatever was removed. It
    // stays empty when removePreviousRoutine() itself throws, which is correct:
    // nothing was removed, so there is nothing to restore.
    let removed = [];

    try {
      // Replace, never stack. See removePreviousRoutine().
      removed = await this.removePreviousRoutine();

      const url = (window.routes || {}).cart_add_url || '/cart/add.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Shopify answers a rejected add with a JSON body carrying a human
        // readable, already-localised `description` ("All 3 … are in your
        // cart."). Far more use to the customer than a generic failure, so it
        // is flagged as showable rather than being parsed and discarded.
        const body = await response.json().catch(() => null);
        // `description` ONLY. `message` is "Cart Error" for a genuine Shopify
        // 422, and for a proxy or WAF sitting in front of the route it can be an
        // internal diagnostic — neither belongs in front of a customer.
        const detail = body && body.description;
        const error = new Error(detail || `HTTP ${response.status}`);
        error.fromShopify = Boolean(detail);
        throw error;
      }

      this.dispatchEvent(
        new CustomEvent('consultation:routine-added', {
          bubbles: true,
          detail: { profile: this.profile, items: lines },
        })
      );

      // Straight to the cart so the customer can check out. An explicit
      // next_url overrides it, but the cart is the default destination.
      this.navigate(this.nextUrl || this.cartUrl);
    } catch (error) {
      console.warn('[Consultation] could not add the routine to the cart', error);

      // The removal already happened. Without this the customer is left with
      // the old routine deleted and the new one not added — they came in with a
      // cart and leave with an empty one, over an error they did not cause.
      await this.restorePreviousRoutine(removed);

      // Shopify's own wording when it gave us one — it names the actual problem
      // (sold out, quantity limit) where the generic message cannot.
      const message = error.fromShopify ? error.message : this.dataset.errorMessage || '';
      this.setStatus(message, true);
      this.setBusy(cont, false);
    }
  }

  /** The one place this element leaves the page. */
  navigate(url) {
    window.location.assign(url);
  }

  setBusy(button, busy) {
    if (!button) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
  }

  /** Announced, not just shown — the button that triggered it keeps focus. */
  setStatus(message, isError = false) {
    const status = this.querySelector('[data-routine-status]');
    if (!status) return;

    // NOT `hidden`. A role="status" that is display:none when the text is
    // written, and only then revealed, is generally not announced — the region
    // has to already be in the accessibility tree when it mutates. It stays
    // rendered and is collapsed visually instead.
    status.textContent = message;
    status.classList.toggle('esalon-consultation-routine__status--empty', !message);
    status.classList.toggle('esalon-consultation-routine__status--error', Boolean(isError));
  }
}

if (!customElements.get('consultation-routine')) {
  customElements.define('consultation-routine', ConsultationRoutine);
}
